<template>
  <div>
    <button type="button" data-testid="whats-new-button" aria-haspopup="true" :aria-expanded="open"
      :aria-label="hasUnseen ? 'What\'s new (new updates)' : 'What\'s new'"
      class="flex gap-3 items-center px-4 w-full rounded-lg cursor-pointer transition-colors duration-200 text-gray-600 hover:bg-primary/10 hover:text-primary"
      :class="mobile ? 'py-3' : 'py-2.5'"
      v-tooltip.right="collapsed ? 'What\'s new' : null" @click="toggle">
      <span class="relative flex">
        <i class="pi pi-gift text-lg"></i>
        <span v-if="hasUnseen" data-testid="whats-new-dot"
          class="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-primary ring-2 ring-white"></span>
      </span>
      <span v-if="!collapsed" class="flex-1 text-left font-medium" :class="mobile ? 'text-base' : 'text-sm'">What's new</span>
    </button>

    <Popover ref="popover" data-testid="whats-new-popover" @show="open = true" @hide="onHide">
      <div class="w-72 max-w-[calc(100vw-3rem)]">
        <p class="px-2 pb-2 text-xs font-semibold uppercase tracking-wider text-surface-500">What's new</p>

        <p v-if="error" class="px-2 py-2 text-sm text-red-600">Updates could not be loaded. Please try again later.</p>
        <p v-else-if="loading && !menuEntries.length" class="px-2 py-2 text-sm text-surface-500">Loading…</p>
        <p v-else-if="!menuEntries.length" class="px-2 py-2 text-sm text-surface-500">Nothing new yet.</p>

        <ol v-else class="relative ml-2 border-l border-dashed border-surface-300">
          <li v-for="entry in menuEntries" :key="entry.id">
            <button type="button" :data-testid="`whats-new-item-${entry.id}`"
              class="relative flex w-full items-start gap-2 rounded-md py-2 pl-4 pr-2 text-left hover:bg-surface-100"
              @click="pickEntry(entry.id)">
              <span class="absolute -left-[5px] top-3.5 h-2 w-2 rounded-full bg-surface-400"></span>
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-medium text-surface-900">{{ entry.title }}</span>
                <span class="block text-xs text-surface-500">{{ formatWhatsNewDate(entry.date) }}</span>
              </span>
              <Tag v-if="unseenIds.includes(entry.id)" value="New" severity="info" class="shrink-0 !text-[10px]" />
            </button>
          </li>
          <li>
            <button type="button" data-testid="whats-new-full-changelog"
              class="relative flex w-full items-center gap-2 rounded-md py-2 pl-4 pr-2 text-left text-sm font-medium text-primary hover:bg-surface-100"
              @click="pickChangelog">
              <span class="absolute -left-[5px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-primary"></span>
              Full changelog
            </button>
          </li>
        </ol>
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import Popover from 'primevue/popover';
import Tag from 'primevue/tag';
import Tooltip from 'primevue/tooltip';
import { useWhatsNew } from '@/composables/whatsNew/useWhatsNew';
import { formatWhatsNewDate } from '@/utils/whatsNew';

withDefaults(defineProps<{
  /** Icon only, for the collapsed desktop sidebar. */
  collapsed?: boolean;
  /** Spacing of the phone menu's rows. */
  mobile?: boolean;
}>(), { collapsed: false, mobile: false });

/** Fired when an update or the changelog opens, so the phone menu can close itself. */
const emit = defineEmits<{ opened: [] }>();

const vTooltip = Tooltip;

const { menuEntries, unseenIds, hasUnseen, markAllSeen, loading, error, store } = useWhatsNew();
const popover = ref<InstanceType<typeof Popover> | null>(null);
const open = ref(false);

const toggle = (event: Event) => popover.value?.toggle(event);

// Tags stay while the menu is open; the dot clears once it closes.
const onHide = () => {
  open.value = false;
  markAllSeen();
};

const pickEntry = (id: string) => {
  popover.value?.hide();
  store.openEntry(id);
  emit('opened');
};

const pickChangelog = () => {
  popover.value?.hide();
  store.openChangelog();
  emit('opened');
};
</script>
